import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { lerConexoes } from "./portalAzos";
import { type ContrapropostaPortal, consultarPortal, useMeuUsuarioCrm } from "./portalAzosAcoes";

/**
 * Exigências: o que a seguradora pede depois da proposta, numa lista só.
 *  - pendências abertas no canônico (`seguros_pendencias`, hoje da MAG; RLS
 *    da 206: super admin ou corretor da proposta);
 *  - contrapropostas da Azos, lidas na hora com o login de quem está logado.
 */

export type PendenciaAberta = {
  seguradora: string;
  id_pendencia: string;
  numero_proposta: string;
  descricao: string | null;
  destinatario: string | null;
  data_envio: string | null;
};

export const lerPendenciasAbertas = async (): Promise<PendenciaAberta[]> => {
  const { data, error } = await supabase
    .from("seguros_pendencias")
    .select("seguradora, id_pendencia, numero_proposta, descricao, destinatario, data_envio")
    .eq("status", "aberta")
    .order("data_envio", { ascending: false, nullsFirst: false })
    .limit(500);
  if (error) throw new Error(error.message);
  return (data ?? []) as PendenciaAberta[];
};

/** Situação do portal ainda não conhecida (lista veio vazia na varredura): fechada é a que diz que foi decidida. */
export const contrapropostaAberta = (c: Pick<ContrapropostaPortal, "situacao">) =>
  !/accept|refus|reject|declin|expir|cancel/i.test(c.situacao ?? "");

/** A própria conexão com a Azos, conectada (contrapropostas só dela). */
const usePropriaConexaoAzos = () => {
  const { data: meuId } = useMeuUsuarioCrm();
  const { data: conexoes } = useQuery({ queryKey: ["azos-conexoes"], queryFn: lerConexoes, staleTime: 60_000 });
  return conexoes?.some((c) => c.crm_user_id === meuId && c.situacao === "conectado") ?? false;
};

export const usePendenciasAbertas = (habilitado = true) =>
  useQuery({ queryKey: ["seguros", "pendencias-abertas"], queryFn: lerPendenciasAbertas, enabled: habilitado, staleTime: 5 * 60_000 });

export const useContrapropostas = (habilitado = true) => {
  const conectado = usePropriaConexaoAzos();
  const consulta = useQuery({
    queryKey: ["portal-azos", "contrapropostas"],
    queryFn: () => consultarPortal<ContrapropostaPortal[]>("contrapropostas"),
    enabled: habilitado && conectado,
    staleTime: 10 * 60_000,
    retry: false,
  });
  return { ...consulta, conectado };
};

/** Quantas exigências abertas: número do item no menu. */
export const useExigenciasAbertas = (habilitado: boolean) => {
  const pendencias = usePendenciasAbertas(habilitado);
  const contra = useContrapropostas(habilitado);
  return (pendencias.data?.length ?? 0) + (contra.data ?? []).filter(contrapropostaAberta).length;
};
